import { StateCreator } from 'zustand';
import { getPanelKeyFromRef } from '@/core';
import { Middleware } from './common';
import { PanelGroupId, PanelGroupSlice } from './panel-group-slice';
import { PanelSlice } from './panel-slice';

/**
 * Slice that handles the visual editor state and related actions for deleting a Panel Group.
 */
export interface DeletePanelGroupSlice {
  deletePanelGroupDialog?: DeletePanelGroupDialogState;

  /**
   * Delete panel group and all the panels within the group
   */
  deletePanelGroup: (panelGroupId: PanelGroupId) => void;

  openDeletePanelGroupDialog: (panelGroupId: PanelGroupId) => void;
  closeDeletePanelGroupDialog: () => void;
}

export interface DeletePanelGroupDialogState {
  panelGroupId: PanelGroupId;
  panelGroupName?: string;
}

export const createDeletePanelGroupSlice: StateCreator<
  // Actions in here need to modify both Panels and Panel Groups state
  DeletePanelGroupSlice & PanelGroupSlice & PanelSlice,
  Middleware,
  [],
  DeletePanelGroupSlice
> = (set, get) => ({
  deletePanelGroup(panelGroupId) {
    const { panelGroups, panelGroupOrder } = get();
    const group = panelGroups[panelGroupId];
    const idIndex = panelGroupOrder.findIndex((id) => id === panelGroupId);
    if (group === undefined || idIndex === -1) {
      throw new Error(`Panel group ${panelGroupId} not found`);
    }

    // Get the panel keys for all the panel items in the group we're going to delete
    let panelKeys: string[] = [];
    if (group.kind === 'Grid') {
      panelKeys = Object.values(group.itemPanelKeys);
    } else if (group.kind === 'SplitPanel') {
      panelKeys = [getPanelKeyFromRef(group.content)];
    }

    set((draft) => {
      // Delete the panel group which also deletes all its items
      delete draft.panelGroups[panelGroupId];
      draft.panelGroupOrder.splice(idIndex, 1);

      // Get all remaining panel keys in use
      const usedPanelKeys = getUsedPanelKeys(draft.panelGroups);

      // For the panel keys of the items that were just deleted, see if they're still used and if not, also delete the
      // panel definition
      for (const panelKey of panelKeys) {
        if (usedPanelKeys.has(panelKey)) continue;

        delete draft.panels[panelKey];
      }
    });
  },

  openDeletePanelGroupDialog: (panelGroupId) => {
    const panelGroup = get().panelGroups[panelGroupId];
    if (panelGroup === undefined) {
      throw new Error(`Panel group with Id ${panelGroupId} not found`);
    }
    set((state) => {
      state.deletePanelGroupDialog = { panelGroupId, panelGroupName: panelGroup.title };
    });
  },

  closeDeletePanelGroupDialog: () =>
    set((state) => {
      state.deletePanelGroupDialog = undefined;
    }),
});

// Helper to get the panel keys of all groups, returning a set to eliminate duplicates
function getUsedPanelKeys(panelGroups: PanelGroupSlice['panelGroups']): Set<string> {
  const usedPanelKeys = new Set<string>();
  for (const group of Object.values(panelGroups)) {
    if (group.kind === 'Grid') {
      for (const panelKey of Object.values(group.itemPanelKeys)) {
        usedPanelKeys.add(panelKey);
      }
    }
    if (group.kind === 'SplitPanel') {
      const panelKey = getPanelKeyFromRef(group.content);
      usedPanelKeys.add(panelKey);
    }
  }
  return usedPanelKeys;
}
